import { useEffect, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import type { LoginOptions } from './api';
import { api } from './session';

/**
 * One hub per environment (2026-10-30): this hub administers `environment`; `peers` are the other environments' hubs.
 * The switch opens the same page on the other hub with `sso=auto`, which starts "Sign in with Microsoft" on arrival
 * when that hub has no session yet (`Login`); Entra's session makes it a few silent redirects.
 */
export const AUTO_SSO = 'sso';

let options: Promise<LoginOptions | null> | null = null;
/** The login options, once per page load (public, also read by the sign-in page). */
export const hubOptions = () => (options ??= api.loginOptions().catch(() => null));

/** Test hook. */
export const resetHubOptions = () => {
  options = null;
};

/** The same page on another hub, asking it to sign in with Microsoft on arrival. */
export function switchUrl(peerUrl: string, location: { pathname: string; search: string }): string {
  const q = new URLSearchParams(location.search);
  q.set(AUTO_SSO, 'auto');
  return `${peerUrl.replace(/\/+$/, '')}${location.pathname}?${q}`;
}

/** Whether this page was opened by another hub's switch. */
export const autoSsoRequested = (search = window.location.search) =>
  new URLSearchParams(search).get(AUTO_SSO) === 'auto';

/** The current address without the switch's marker (where to come back to, what to show in the address bar). */
export function withoutAutoSso(pathname: string, search: string): string {
  const q = new URLSearchParams(search);
  q.delete(AUTO_SSO);
  const rest = q.toString();
  return `${pathname}${rest ? `?${rest}` : ''}`;
}

/** Above the menu: the environment this hub administers and a link to the same page on each other environment's hub. */
export function EnvironmentSwitch({ pathname, search }: { pathname: string; search: string }) {
  const [o, setO] = useState<LoginOptions | null>(null);
  useEffect(() => {
    let live = true;
    void hubOptions().then((v) => live && setO(v));
    return () => {
      live = false;
    };
  }, []);
  if (!o?.environment && !o?.peers?.length) return null;
  return (
    <div className="oph-env" data-testid="environment-switch">
      {o.environment && (
        <span className="oph-env-current" data-env={o.environment.id}>
          {o.environment.label}
        </span>
      )}
      {o.peers?.map((p) => (
        <a key={p.url} className="oph-env-switch" href={switchUrl(p.url, { pathname, search })}>
          <ArrowLeftRight size={14} aria-hidden="true" />
          Switch to {p.label}
        </a>
      ))}
    </div>
  );
}
